import { useState } from 'react';

import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import { DatePeriodInTimestamp } from '@cast/design-system';
import { OptionalTimeRange } from '@cast/shared-types';

import {
  DateRangeFilter,
  RangePickerQueryParamKeys,
} from '../types/time-range';

import { isValidDateRange } from './utils';

type RangePickerState = {
  currentDateRange: OptionalTimeRange<string>;
  currentDateRangeFilter?: DateRangeFilter;
  timezone?: string;
};

type ManageRangePickerState = {
  getState: () => RangePickerState;
  setState: (state: RangePickerState) => void;
};

type UseReactRouterStrategyArgs = {
  initialRange?: DatePeriodInTimestamp;
  initialPreset?: string;
  initialTimezone?: string;
  queryParamKeys: RangePickerQueryParamKeys;
  maxNumberOfDays?: number;
};

export const useReactRouterStrategy = ({
  initialRange,
  initialPreset,
  initialTimezone,
  queryParamKeys,
  maxNumberOfDays,
}: UseReactRouterStrategyArgs): ManageRangePickerState => {
  const [searchParams, setSearchParams] = useSearchParams();

  const from = searchParams.get(queryParamKeys.fromDate) || initialRange?.from;
  const to = searchParams.get(queryParamKeys.toDate) || initialRange?.to;
  const preset =
    !from && !to
      ? ((searchParams.get(queryParamKeys.dateRangePreset) ??
          initialPreset) as DateRangeFilter)
      : undefined;
  const timezone = searchParams.get(queryParamKeys.timeZone) || initialTimezone;

  return {
    getState: () => {
      return {
        currentDateRange: { from, to },
        currentDateRangeFilter: preset,
        timezone,
      };
    },
    setState: (state: RangePickerState) => {
      setSearchParams((prev) => {
        // If preset is explicitly set, we prefer that over the date range
        if (state.currentDateRangeFilter) {
          prev.set(
            queryParamKeys.dateRangePreset,
            state.currentDateRangeFilter
          );
          prev.delete(queryParamKeys.fromDate);
          prev.delete(queryParamKeys.toDate);
        } else {
          // If preset is not set, we use the date range
          if (
            state.currentDateRange?.from &&
            state.currentDateRange?.to &&
            isValidDateRange(
              dayjs(state.currentDateRange.from),
              dayjs(state.currentDateRange.to),
              maxNumberOfDays
            )
          ) {
            prev.set(queryParamKeys.fromDate, state.currentDateRange.from);
            prev.set(queryParamKeys.toDate, state.currentDateRange.to);
            prev.delete(queryParamKeys.dateRangePreset);
          } else {
            // If neither preset nor date range is set or valid, we clear the params
            prev.delete(queryParamKeys.fromDate);
            prev.delete(queryParamKeys.toDate);
            prev.delete(queryParamKeys.dateRangePreset);
          }
        }

        if (state.timezone) {
          prev.set(queryParamKeys.timeZone, state.timezone);
        } else {
          prev.delete(queryParamKeys.timeZone);
        }

        return prev;
      });
    },
  };
};

type UseReactStateStrategyArgs = {
  initialRange?: DatePeriodInTimestamp;
  initialPreset?: string;
  initialTimezone?: string;
  maxNumberOfDays?: number;
};

export const useReactStateStrategy = ({
  initialRange,
  initialPreset,
  initialTimezone,
  maxNumberOfDays,
}: UseReactStateStrategyArgs): ManageRangePickerState => {
  const [state, setState] = useState<RangePickerState>({
    currentDateRange: { from: initialRange?.from, to: initialRange?.to },
    currentDateRangeFilter: !initialRange
      ? (initialPreset as DateRangeFilter)
      : undefined,
    timezone: initialTimezone,
  });

  return {
    getState: () => {
      return state;
    },
    setState: (state: RangePickerState) => {
      setState((prev) => {
        const newState = {
          ...prev,
        };

        // If preset is explicitly set, we prefer that over the date range
        if (state.currentDateRangeFilter) {
          newState.currentDateRangeFilter = state.currentDateRangeFilter;
          newState.currentDateRange = { from: undefined, to: undefined };
        } else {
          // If preset is not set, we use the date range
          if (
            state.currentDateRange?.from &&
            state.currentDateRange?.to &&
            isValidDateRange(
              dayjs(state.currentDateRange.from),
              dayjs(state.currentDateRange.to),
              maxNumberOfDays
            )
          ) {
            newState.currentDateRange = state.currentDateRange;
            newState.currentDateRangeFilter = undefined;
          } else {
            // If neither preset nor date range is set or valid, we clear the params
            newState.currentDateRange = { from: undefined, to: undefined };
            newState.currentDateRangeFilter = undefined;
          }
        }

        if (state.timezone) {
          newState.timezone = state.timezone;
        } else {
          newState.timezone = undefined;
        }

        return newState;
      });
    },
  };
};
